import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../lib/api';
import type { Section } from '../lib/api';

const Sections: React.FC = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<Section[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [publishedFilter, setPublishedFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Debounce search term to avoid API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSections = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (publishedFilter !== 'all') {
        params.published = publishedFilter === 'published';
      }

      console.log('🔍 Fetching sections with params:', params);
      const response = await apiClient.getSections(params);
      console.log('✅ Sections response:', response);
      
      setSections(response.data || []);
      
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination!.total,
          totalPages: response.pagination!.totalPages,
        }));
      }
    } catch (error: any) {
      console.error('Failed to fetch sections:', error);
      setError(error.message || 'Failed to load sections');
      setSections([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [debouncedSearchTerm, publishedFilter, pagination.page]);

  const handleTogglePublish = async (section: Section) => {
    try {
      console.log('🔄 Toggling publish status for:', section.id);
      await apiClient.toggleSectionPublish(section.id, !section.isPublished);
      
      // Update local state
      setSections(prevSections => 
        prevSections.map(s => 
          s.id === section.id 
            ? { ...s, isPublished: !s.isPublished }
            : s
        )
      );
      
      console.log('✅ Publish status toggled successfully');
    } catch (error: any) {
      console.error('Failed to toggle publish status:', error);
      // For now, still update local state as fallback
      setSections(prevSections => 
        prevSections.map(s => 
          s.id === section.id 
            ? { ...s, isPublished: !s.isPublished }
            : s
        )
      );
    }
  };

  const handleDelete = async (section: Section) => {
    if (!window.confirm(`Are you sure you want to delete "${section.title}"?`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting section:', section.id);
      await apiClient.deleteSection(section.id);
      
      // Update local state
      setSections(prevSections => 
        prevSections.filter(s => s.id !== section.id)
      );
      
      console.log('✅ Section deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete section:', error);
      // For now, still update local state as fallback
      setSections(prevSections => 
        prevSections.filter(s => s.id !== section.id)
      );
    }
  };

  const handleEdit = (section: Section) => {
    navigate(`/sections/${section.id}/edit`);
  };

  const handleCreate = () => {
    navigate('/sections/new');
  };

  const SectionCard: React.FC<{ section: Section }> = ({ section }) => (
    <div className="admin-card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-medium text-gray-9 text-lg">{section.title}</h3>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                section.isPublished 
                  ? 'bg-success-50 text-success-600' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {section.isPublished ? 'Published' : 'Draft'}
              </span>
              {section.isVideo && (
                <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
                  Video
                </span>
              )}
            </div>
          </div>
          
          {section.shortDescription && (
            <p className="text-gray-6 text-sm mb-3 line-clamp-2">
              {section.shortDescription}
            </p>
          )}
          
          <div className="flex items-center justify-between text-xs text-gray-5">
            <div className="flex items-center space-x-4">
              <span>Views: {section.viewCount}</span>
              <span>Images: {section.images?.length || 0}</span>
              <span>Updated: {new Date(section.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        {section.featuredImage && (
          <img
            src={section.featuredImage.url}
            alt={section.featuredImage.alt || section.title}
            className="w-20 h-20 rounded-lg object-cover ml-4 flex-shrink-0"
          />
        )}
      </div>
      
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-3">
        <div className="flex space-x-2">
          <button
            onClick={() => handleTogglePublish(section)}
            className="flex items-center px-3 py-1.5 text-sm rounded-lg hover:bg-gray-1 transition-colors"
          >
            {section.isPublished ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            <span className="ml-1">
              {section.isPublished ? 'Unpublish' : 'Publish'}
            </span>
          </button>
          
          <button
            onClick={() => handleEdit(section)}
            className="flex items-center px-3 py-1.5 text-sm rounded-lg hover:bg-gray-1 transition-colors"
          >
            <EditOutlined />
            <span className="ml-1">Edit</span>
          </button>
        </div>
        
        <button
          onClick={() => handleDelete(section)}
          className="flex items-center px-3 py-1.5 text-sm text-error rounded-lg hover:bg-red-50 transition-colors"
        >
          <DeleteOutlined />
          <span className="ml-1">Delete</span>
        </button>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-9">Sections</h1>
        <div className="admin-card text-center py-12">
          <div className="text-error text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-medium text-gray-9 mb-2">Failed to Load Sections</h2>
          <p className="text-gray-6 mb-4">{error}</p>
          <button
            onClick={fetchSections}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <ReloadOutlined className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-9">Sections</h1>
          <p className="text-gray-6 mt-1">
            Manage your content sections and publications
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <PlusOutlined className="mr-2" />
          New Section
        </button>
      </div>

      {/* Search and Filter */}
      <div className="admin-card">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-5" />
            <input
              type="text"
              placeholder="Search sections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-4 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 transition-colors"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <FilterOutlined className="text-gray-5" />
            <div className="relative">
              <select
                value={publishedFilter}
                onChange={(e) => setPublishedFilter(e.target.value as 'all' | 'published' | 'draft')}
                className="appearance-none bg-white px-3 py-2 pr-8 border border-gray-4 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 transition-colors cursor-pointer"
              >
                <option value="all">All Sections</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
              {/* Custom dropdown arrow */}
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          <button
            onClick={fetchSections}
            className="p-2 text-gray-5 hover:text-gray-7 transition-colors"
            title="Refresh"
          >
            <ReloadOutlined />
          </button>
        </div>
        
        {/* Stats */}
        <div className="mt-4 pt-4 border-t border-gray-3 flex items-center justify-between">
          <p className="text-sm text-gray-6">
            Showing {sections.length} sections
            {pagination.total > 0 && ` of ${pagination.total} total`}
          </p>
          
          {sections.length > 0 && (
            <div className="flex items-center space-x-4 text-sm text-gray-6">
              <span>{sections.filter(s => s.isPublished).length} published</span>
              <span>{sections.filter(s => !s.isPublished).length} drafts</span>
              <span>{sections.filter(s => s.isVideo).length} with video</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="admin-card animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-6 bg-gray-3 rounded mb-2"></div>
                  <div className="h-4 bg-gray-3 rounded mb-2 w-3/4"></div>
                  <div className="h-4 bg-gray-3 rounded w-1/2"></div>
                </div>
                <div className="w-20 h-20 bg-gray-3 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      ) : sections.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sections.map((section) => (
            <SectionCard key={section.id} section={section} />
          ))}
        </div>
      ) : (
        <div className="admin-card text-center py-12">
          <div className="text-6xl mb-4">📄</div>
          <h2 className="text-lg font-medium text-gray-7 mb-2">
            {searchTerm || publishedFilter !== 'all' ? 'No Matching Sections' : 'No Sections Yet'}
          </h2>
          <p className="text-gray-5 mb-4">
            {searchTerm || publishedFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Start creating content sections for your Coruscate Creative Studio.'
            }
          </p>
          {(!searchTerm && publishedFilter === 'all') && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <PlusOutlined className="mr-2" />
              Create First Section
            </button>
          )}
        </div>
      )}

      {/* Pagination (for future use) */}
      {pagination.totalPages > 1 && (
        <div className="admin-card">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-6">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-4 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-gray-4 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sections;